import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, unwrap } from "@/lib/api";
import { COUNTRIES, countryFlag } from "@/lib/countries";

const blank = () => ({ name: "", country: "US", state: "MA", cities: [], zip_codes: [], zone_surcharge: 0, is_active: true, display_order: 99 });

export default function AdminZones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {idx, data}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/zones");
      setZones(unwrap(r) || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (z) => {
    const body = {
      name: z.name,
      country: z.country || "US",
      state: z.state,
      cities: z.cities,
      zip_codes: z.zip_codes,
      zone_surcharge: Number(z.zone_surcharge) || 0,
      is_active: !!z.is_active,
      display_order: Number(z.display_order) || 0,
    };
    try {
      if (z.id) await api.patch(`/admin/zones/${z.id}`, body);
      else await api.post("/admin/zones", body);
      toast.success("Zone saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail?.message || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this zone?")) return;
    try { await api.delete(`/admin/zones/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const moveZone = async (idx, delta) => {
    const next = idx + delta;
    if (next < 0 || next >= zones.length) return;
    const reordered = [...zones];
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    // Optimistic update + persist new display_order
    const withOrder = reordered.map((z, i) => ({ ...z, display_order: i + 1 }));
    setZones(withOrder);
    try {
      await api.patch("/admin/zones/reorder", { items: withOrder.map((z, i) => ({ id: z.id, display_order: i + 1 })) });
    } catch {
      toast.error("Reorder failed");
      load();
    }
  };

  if (loading) return <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="admin-zones">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Zones</h1>
          <p className="text-[#4B5563] text-sm mt-1">{zones.length} zones configured · use ↑↓ to reorder</p>
        </div>
        <Button onClick={() => setEditing({ idx: -1, data: blank() })} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="add-zone-btn">
          <Plus className="w-4 h-4 mr-1" /> Add zone
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {zones.map((z, i) => (
          <Card key={z.id} className="rounded-2xl p-5 border border-[#E5E7EB]" data-testid={`zone-row-${z.state}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-[#0EA5A4] font-bold flex items-center gap-1.5">
                  <span>{countryFlag(z.country || "US")}</span>
                  <span>{z.country || "US"}{z.state ? ` · ${z.state}` : ""}</span>
                </div>
                <div className="font-display text-lg font-bold mt-1">{z.name}</div>
                <div className="text-xs text-[#4B5563] mt-1">{(z.cities || []).join(", ")}</div>
                <div className="text-xs text-[#9CA3AF] mt-1">{(z.zip_codes || []).slice(0, 5).join(", ")}{(z.zip_codes || []).length > 5 ? "…" : ""}</div>
              </div>
              <div className="text-right">
                <div className="font-mono font-bold text-[#0EA5A4]">+${(z.zone_surcharge || 0).toFixed(2)}</div>
                <div className={`text-[10px] font-bold uppercase tracking-wider mt-1 ${z.is_active ? "text-emerald-600" : "text-stone-400"}`}>{z.is_active ? "Active" : "Inactive"}</div>
              </div>
            </div>
            <div className="mt-4 flex gap-2 items-center">
              <Button onClick={() => setEditing({ idx: i, data: { ...z, cities: z.cities || [], zip_codes: z.zip_codes || [] } })}
                variant="outline" size="sm" className="rounded-xl" data-testid={`edit-zone-${z.state}`}>Edit</Button>
              <Button onClick={() => remove(z.id)} variant="outline" size="sm" className="rounded-xl text-red-500" data-testid={`delete-zone-${z.state}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
              <div className="ml-auto flex items-center gap-1">
                <Button onClick={() => moveZone(i, -1)} disabled={i === 0} variant="outline" size="icon" className="rounded-xl h-8 w-8" data-testid={`zone-up-${z.state}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button onClick={() => moveZone(i, 1)} disabled={i === zones.length - 1} variant="outline" size="icon" className="rounded-xl h-8 w-8" data-testid={`zone-down-${z.state}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {editing && <ZoneEditor data={editing.data} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ZoneEditor({ data, onCancel, onSave }) {
  const [z, setZ] = useState({
    ...data,
    cities_text: (data.cities || []).join(", "),
    zips_text: (data.zip_codes || []).join(", "),
  });
  const update = (patch) => setZ((s) => ({ ...s, ...patch }));

  const submit = () => {
    const final = {
      ...z,
      cities: z.cities_text.split(",").map((s) => s.trim()).filter(Boolean),
      zip_codes: z.zips_text.split(",").map((s) => s.trim()).filter(Boolean),
    };
    onSave(final);
  };

  return (
    <Card className="rounded-2xl p-6 border border-[#0EA5A4]" data-testid="zone-editor">
      <h3 className="font-display font-bold mb-4">{data.id ? "Edit zone" : "New zone"}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Name</Label>
          <Input value={z.name} onChange={(e) => update({ name: e.target.value })} className="rounded-xl mt-1" data-testid="zone-name" />
        </div>
        <div>
          <Label>Country</Label>
          <select value={z.country || "US"} onChange={(e) => update({ country: e.target.value })}
            className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 bg-white" data-testid="zone-country">
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.name_en} ({c.code})</option>)}
          </select>
        </div>
        <div>
          <Label>State / Province</Label>
          <Input value={z.state} onChange={(e) => update({ state: e.target.value })}
            placeholder="MA" className="rounded-xl mt-1 font-mono uppercase" data-testid="zone-state" />
        </div>
        <div className="md:col-span-2">
          <Label>Cities (comma-separated)</Label>
          <Input value={z.cities_text} onChange={(e) => update({ cities_text: e.target.value })} className="rounded-xl mt-1" data-testid="zone-cities" />
        </div>
        <div className="md:col-span-2">
          <Label>ZIP codes (comma-separated)</Label>
          <Input value={z.zips_text} onChange={(e) => update({ zips_text: e.target.value })} className="rounded-xl mt-1 font-mono" data-testid="zone-zips" />
        </div>
        <div>
          <Label>Surcharge ($)</Label>
          <Input type="number" step="0.01" value={z.zone_surcharge}
            onChange={(e) => update({ zone_surcharge: e.target.value })}
            className="rounded-xl mt-1 font-mono" data-testid="zone-surcharge" />
        </div>
        <div>
          <Label>Display order</Label>
          <Input type="number" value={z.display_order}
            onChange={(e) => update({ display_order: e.target.value })}
            className="rounded-xl mt-1 font-mono" data-testid="zone-order" />
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={z.is_active} onCheckedChange={(v) => update({ is_active: v })} data-testid="zone-active" />
          <Label>Active</Label>
        </div>
      </div>

      <div className="mt-5 flex gap-2">
        <Button onClick={submit} className="rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="zone-save">
          <Save className="w-4 h-4 mr-2" /> Save
        </Button>
        <Button onClick={onCancel} variant="outline" className="rounded-xl" data-testid="zone-cancel">Cancel</Button>
      </div>
    </Card>
  );
}
